/**
 * PositionAssignmentPanel — inline position allocation for a seat, driven
 * through the SAME `position_transfer` saga API HR-Ops uses.
 *
 *   - mode 'transfer' (filled seat): the incumbent is fixed; the operator picks
 *     a VACANT target position to move them into.
 *   - mode 'assign' (vacant seat): the target position is fixed (this seat); the
 *     operator picks the worker to place into it.
 *
 * Both resolve to `submitMutation(workerPn, { mutation_type:'position_transfer',
 * effective_date, fields_after:{ TargetPositionId, EffectiveDate } })`, with the
 * same date in both places.
 *
 * Soft-gate: if the saga fails with `fo_error.stage ===
 * 'preflight_no_position_defaults'` (the target has no default financial
 * dimensions), a checkbox appears and re-submits with
 * `options.proceed_without_position_defaults = true`, accepting that the worker
 * lands with empty dimensions until corrected.
 */

import React, { useMemo, useState } from 'react';
import { ArrowRightLeft, CheckCircle2, Loader2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import organizationService, {
  type Position,
} from '@/services/api/organizationService';
import {
  integrationService,
  type EmployeeData,
} from '@/services/api/integrationService';
import { AsyncSearchCombobox } from './AsyncSearchCombobox';
import { MutationSagaStatus } from './MutationSagaStatus';
import { useMutationSaga } from '@/hooks/useMutationSaga';
import { useOccupancyRefresh } from '@/hooks/useOccupancyRefresh';
import { sastToday } from '@/components/admin/lifecycle/spellCoverage';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const NO_DEFAULTS_STAGE = 'preflight_no_position_defaults';

export interface PositionAssignmentPanelProps {
  mode: 'transfer' | 'assign';
  seatPositionCode: string;
  seatPositionTitle?: string;
  incumbentPn?: string;
  incumbentName?: string;
  onDone: () => void;
  /** HR-Ops fallback deep-link + opener (used on a 403). */
  hrOpsUrl?: string | null;
  onOpenHrOps?: (url: string) => void;
}

export const PositionAssignmentPanel: React.FC<PositionAssignmentPanelProps> = ({
  mode,
  seatPositionCode,
  seatPositionTitle,
  incumbentPn,
  incumbentName,
  onDone,
  hrOpsUrl,
  onOpenHrOps,
}) => {
  // South African day: the UTC day is still yesterday until 02:00 SAST.
  const today = useMemo(() => sastToday(), []);
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [targetPosition, setTargetPosition] = useState<Position | null>(null);
  const [worker, setWorker] = useState<EmployeeData | null>(null);
  const [proceedWithoutDefaults, setProceedWithoutDefaults] = useState(false);
  const saga = useMutationSaga();

  const isTransfer = mode === 'transfer';

  // Transfer: pick a VACANT target (never the current seat).
  const searchPositions = useMemo(
    () => async (q: string) => {
      const positions = await organizationService.listPositions({
        is_vacant: true,
        query: q,
        limit: 20,
      });
      return positions.filter((p) => p.position_code !== seatPositionCode);
    },
    [seatPositionCode],
  );

  // Assign: pick the worker to place into this vacant seat. Restrict to F&O
  // workers (includeHrOnly:false drops HR-only directory rows that have no F&O
  // account and could never be assigned). We do NOT further filter by "has a
  // live assignment": the /employees/search response is built from the Users
  // table and carries no reliable current-position/employment signal, so a
  // worker who lacks one still hard-fails F&O preflight
  // (`preflight_no_employeesv2sage_row`) — now surfaced clearly by MutationSagaStatus.
  const searchWorkers = useMemo(
    () => async (q: string) => {
      const res = await integrationService.searchEmployees(q, 20, {
        status: 'active',
        includeHrOnly: false,
      });
      return res.employees ?? [];
    },
    [],
  );

  const workerPn = isTransfer ? incumbentPn : worker?.personnel_number;
  const targetCode = isTransfer ? targetPosition?.position_code : seatPositionCode;

  const dateValid = ISO_DATE.test(effectiveDate);
  const selectionValid = isTransfer
    ? !!targetPosition && targetPosition.position_code !== seatPositionCode
    : !!worker;
  const formValid = dateValid && selectionValid && !!workerPn && !!targetCode;

  const softGate = saga.foErrorStage === NO_DEFAULTS_STAGE;

  const submit = () => {
    if (!formValid || !workerPn || !targetCode) return;
    saga.submit(workerPn, {
      mutation_type: 'position_transfer',
      // Same date at top level: the overnight run completes a future-dated
      // move by the stored effective date, and the server refuses a mismatch.
      effective_date: effectiveDate,
      fields_after: {
        TargetPositionId: targetCode,
        EffectiveDate: effectiveDate,
      },
      options: proceedWithoutDefaults
        ? { proceed_without_position_defaults: true }
        : {},
      reason: `Position ${isTransfer ? 'transfer' : 'assignment'}: ${
        isTransfer ? incumbentName ?? incumbentPn : worker?.full_name ?? workerPn
      } → ${targetCode} effective ${effectiveDate}`,
    });
  };

  const sagaDone = saga.phase === 'completed' || saga.phase === 'partial';
  // Transfer touches BOTH the source seat (vacated) and the target seat
  // (filled); assign touches just this seat. Re-read whichever changed.
  const affectedCodes = useMemo(
    () =>
      (isTransfer
        ? [seatPositionCode, targetPosition?.position_code]
        : [seatPositionCode]
      ).filter((c): c is string => !!c),
    [isTransfer, seatPositionCode, targetPosition?.position_code],
  );
  const refresh = useOccupancyRefresh(sagaDone, affectedCodes);
  // F&O honours a future EffectiveDate: today's re-read still shows the OLD
  // occupant (the projection correctly stays pending), so don't claim an
  // instant change — say it's scheduled instead.
  const isFutureDated = dateValid && effectiveDate > today;

  // ── Success ──────────────────────────────────────────────────────────────
  if (sagaDone) {
    const partial = saga.phase === 'partial';
    const targetLabel = isTransfer
      ? targetPosition?.title ?? targetCode
      : seatPositionTitle ?? seatPositionCode;
    const workerLabel = isTransfer
      ? incumbentName ?? incumbentPn
      : worker?.full_name ?? workerPn;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {partial
                ? 'F&O updated — Sage needs follow-up'
                : isTransfer
                  ? 'Transfer submitted'
                  : 'Assignment submitted'}
            </p>
            <p className="mt-0.5">
              {partial
                ? 'The position change was written to F&O, but the Sage side reported an issue (see below). Payroll will need a manual follow-up.'
                : `${workerLabel} → ${targetLabel} (effective ${effectiveDate}).`}
            </p>
            <p className="mt-1.5 text-xs opacity-90">
              {isFutureDated
                ? `F&O updated — scheduled for ${effectiveDate}. The seat, the worker's Campus / Department / Faculty and Sage update on that date (Sage waits if Payroll's month-end close is on then).`
                : refresh.state === 'ok'
                  ? 'F&O updated — occupancy here is refreshed.'
                  : refresh.state === 'error'
                    ? 'F&O updated — the org chart / occupancy here refreshes after the next nightly sync.'
                    : 'F&O updated — refreshing occupancy here…'}
            </p>
          </div>
        </div>

        <MutationSagaStatus
          phase={saga.phase}
          mutation={saga.mutation}
          error={saga.error}
          errorStatus={saga.errorStatus}
          hrOpsUrl={hrOpsUrl}
          onOpenHrOps={onOpenHrOps}
        />

        <Button
          type="button"
          className="w-full sm:w-auto"
          onClick={async () => {
            // Let the targeted re-read land before the drawer refetches.
            await refresh.settle();
            onDone();
          }}
        >
          Done
        </Button>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-heritage-blue/10 p-2 text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
          {isTransfer ? (
            <ArrowRightLeft className="h-5 w-5" />
          ) : (
            <UserPlus className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">
            {isTransfer ? 'Transfer to another position' : 'Assign someone to this seat'}
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {isTransfer
              ? `Move ${incumbentName ?? incumbentPn} into a vacant position. Title and financial dimensions are inherited from the target.`
              : `Place a worker into ${seatPositionTitle ?? seatPositionCode}.`}
          </p>
        </div>
      </div>

      {isTransfer ? (
        <div className="space-y-1.5">
          <Label>Target position (vacant)</Label>
          <AsyncSearchCombobox<Position>
            search={searchPositions}
            getKey={(p) => String(p.id)}
            getPrimary={(p) => p.title}
            getSecondary={(p) =>
              [p.position_code, p.campus_name ?? p.campus_code]
                .filter(Boolean)
                .join(' · ')
            }
            onSelect={setTargetPosition}
            placeholder="Search vacant positions…"
            disabled={saga.isBusy}
            emptyText="No vacant positions match."
          />
          {targetPosition && (
            <p className="text-xs text-muted-foreground">
              Selected:{' '}
              <span className="font-medium text-foreground">{targetPosition.title}</span>{' '}
              ({targetPosition.position_code})
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>Worker</Label>
          <AsyncSearchCombobox<EmployeeData>
            search={searchWorkers}
            getKey={(e) => e.personnel_number}
            getPrimary={(e) => e.full_name}
            getSecondary={(e) =>
              [e.personnel_number, e.worker_status].filter(Boolean).join(' · ')
            }
            onSelect={setWorker}
            placeholder="Search active workers by name…"
            disabled={saga.isBusy}
            emptyText="No active workers match."
          />
          {worker ? (
            <p className="text-xs text-muted-foreground">
              Selected:{' '}
              <span className="font-medium text-foreground">{worker.full_name}</span>{' '}
              ({worker.personnel_number})
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Search active F&amp;O workers by name. A worker whose employment
              isn’t live yet can’t be placed — use the full slip in HR-Ops.
            </p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="pa-effective-date">Effective date</Label>
        <Input
          id="pa-effective-date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          disabled={saga.isBusy}
          className="w-full sm:w-48"
        />
        {!dateValid && (
          <p className="text-xs text-destructive">Enter a valid date (YYYY-MM-DD).</p>
        )}
      </div>

      {/* Soft-gate — target has no default dimensions. */}
      {softGate && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          <p>
            The target position has no default financial dimensions (Campus /
            Department / Faculty) configured. You can proceed, but the worker will
            land with empty dimensions until they’re corrected manually.
          </p>
          <label className="flex items-center gap-2 font-medium">
            <Checkbox
              checked={proceedWithoutDefaults}
              onCheckedChange={setProceedWithoutDefaults}
              disabled={saga.isBusy}
            />
            Proceed anyway — worker will have empty dimensions
          </label>
        </div>
      )}

      <MutationSagaStatus
        phase={saga.phase}
        mutation={saga.mutation}
        error={saga.error}
        errorStatus={saga.errorStatus}
        hrOpsUrl={hrOpsUrl}
        onOpenHrOps={onOpenHrOps}
      />

      <Button
        type="button"
        className="w-full sm:w-auto"
        onClick={submit}
        disabled={!formValid || saga.isBusy || (softGate && !proceedWithoutDefaults)}
      >
        {saga.isBusy ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            {saga.phase === 'submitting' ? 'Submitting…' : 'Applying…'}
          </>
        ) : softGate ? (
          <>
            <ArrowRightLeft className="mr-1.5 h-4 w-4" />
            Retry with empty dimensions
          </>
        ) : isTransfer ? (
          <>
            <ArrowRightLeft className="mr-1.5 h-4 w-4" />
            Transfer
          </>
        ) : (
          <>
            <UserPlus className="mr-1.5 h-4 w-4" />
            Assign
          </>
        )}
      </Button>
    </div>
  );
};

export default PositionAssignmentPanel;
